/**
 * Collection Filters
 * 
 * @module collection-filters
 * @version 1.0.0
 * @extends HTMLElement
 */
class CollectionFilters extends HTMLElement {

    /**
     * Collection filters web component.
     * 
     * @constructor
     */
    constructor() {

      super();

      // This variable is used to store data as cache
      this.filterData = [];

      // Aborts any in-flight fetch when a newer one starts (prevents a slow
      // response from overwriting the grid with stale state).
      this.currentController = null;
    }

    connectedCallback() {
      if (this._initialized || !this.querySelector('form')) return;
      this._initialized = true;

      this.initEvents();

      this.renderCounts();

      this.renderAllSelectedFilters();
    }

    /**
     * Init component events.
     * 
     * @returns {undefined}
     */
    initEvents() {

        if(window.screenMode != 'mobile' ) {
            this.setTop();
        }

        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

        this.debouncedOnSubmit = window.theme.debounce.apply(event => {
            this.onSubmitHandler(event);
        }, 500);

        // Drawer consumers (standard drawer, golden-goose) carry a dedicated
        // [data-filter-apply] marker on the "Apply Filters" button. In that mode
        // we batch selections and show the pending selection live in the chips/count
        // as pills toggle (no fetch). Closing the drawer by ANY means — the Apply
        // button, the header X, Esc, or tapping outside — COMMITS the selection
        // (see observeDrawerClose); nothing is silently discarded. Brand-tag has no
        // apply button, so keep the original per-change auto-apply.
        const applyButton = this.querySelector('[data-filter-apply]');
        if (applyButton) {
            applyButton.addEventListener('click', this.onApplyHandler.bind(this));
            this.querySelector('form').addEventListener('change', this.onFilterToggle.bind(this));
            const activeFacets = this.querySelector('.active-facets');
            if (activeFacets) {
                activeFacets.addEventListener('click', this.onPendingFacetRemove.bind(this));
            }
            this.observeDrawerClose();
        } else {
            this.querySelector('form').addEventListener('input', this.debouncedOnSubmit.bind(this));
        }

        window.addEventListener('popstate', this.onHistoryChange.bind(this));
    }
    /**
     * Scroll the page update
     * @returns {undefined}
     */
    scrollUp(){
        if (document.getElementById('CollectionProductGrid').dataset.scroll == "true") {
            window.scrollTo({ top: 50, behavior: 'smooth' });
            // For Safari
            document.body.scrollTop = 50;
            // For Chrome, Firefox, IE and Opera
            document.documentElement.scrollTop = 50;
        }
    }
    
    /**
     * On submit handler event.
     * 
     * @param {object} event Event.
     * @returns {undefined}
     */
    onSubmitHandler(event) {

      event.preventDefault();

      const formData = new FormData(event.target.closest('form'));

      const searchParams = new URLSearchParams(formData).toString();

      this.renderPage(searchParams, event);
    }

    /**
     * On Apply Filters click — batch-apply the current form state in one fetch.
     * Reads the whole form (not a per-pill event) and passes event = null so
     * renderFilters re-renders every pane from the authoritative server HTML.
     *
     * @param {object} event Click event from [data-filter-close].
     * @returns {undefined}
     */
    onApplyHandler(event) {

        // The Apply button is <button data-filter-apply> with no type, inside the
        // filter <form> — a native click would submit the form and reload the page.
        // Prevent that and apply via AJAX. Also called (with no event) by the
        // drawer-close observer so that closing the drawer ANY way commits the
        // selection; clearing the dirty flag stops that close from re-applying.
        if (event) event.preventDefault();

        const form = this.querySelector('form');

        if (!form) return;

        this._filtersDirty = false;

        const searchParams = new URLSearchParams(new FormData(form)).toString();

        this.renderPage(searchParams, null);
    }

    /**
     * On filter pill toggle — optimistic, client-side only (NO fetch).
     * Rebuilds the active-facets chips + count from the currently-checked
     * checkboxes so the shopper gets instant feedback before Apply.
     *
     * @param {object} event Change event from a checkbox in the form.
     * @returns {undefined}
     */
    onFilterToggle(event) {

        this._filtersDirty = true;

        this.renderOptimisticFacets();
    }

    /**
     * Rebuild the active-facets chips and the (N) count from the current
     * checkbox state, without any network round-trip. Shows the shopper's
     * PENDING selection live. Excludes the store-forced "In stock" default
     * (matching the Phase-1 chip-hiding) and preserves any applied price chip.
     *
     * @returns {undefined}
     */
    renderOptimisticFacets() {

        const container = this.querySelector('.active-facets');

        if (!container) return;

        const countEl = container.querySelector('#activeFacetsCount');

        // Remove existing non-price chips (both server <facet-remove> and our own
        // pending spans), but PRESERVE the price chip. The price chip is a
        // <facet-remove> whose <a> carries .active-facets__button--light, so detect
        // and keep those; the #activeFacetsCount div is always kept.
        const existingChips = container.querySelectorAll('facet-remove, [data-pending-facet]');
        let hasPriceChip = false;
        existingChips.forEach((chip) => {
            const isPrice = chip.querySelector('.active-facets__button--light');
            if (isPrice) {
                hasPriceChip = true;
            } else {
                chip.remove();
            }
        });

        // Build a pending chip per checked FACET checkbox. Only real filters
        // (name starts with "filter.") count — this excludes the Sorting cards
        // (name="sort_by", always has one checked) so sort never shows as a chip
        // or inflates the count. The store-forced "In stock" is also excluded.
        const checked = Array.from(this.querySelectorAll('input[type="checkbox"]:checked'))
            .filter((cb) => cb.name && cb.name.indexOf('filter.') === 0)
            .filter((cb) => !(cb.name === 'filter.v.availability' && cb.value === '1'));

        checked.forEach((cb) => {

            const label = this.querySelector('label[for="' + cb.id + '"]');
            const labelText = label ? label.textContent.trim() : cb.value;

            const chip = document.createElement('span');
            chip.setAttribute('data-pending-facet', '');

            const link = document.createElement('a');
            link.href = '#';
            link.className = 'active-facets__button';
            link.setAttribute('data-pending-remove', cb.id);
            // Set the label via textContent to avoid any markup injection from
            // user-visible filter values, then append the close icon as markup.
            link.textContent = labelText;
            link.insertAdjacentHTML('beforeend', CollectionFilters.CLOSE_ICON_SVG);

            chip.appendChild(link);

            if (countEl) {
                container.insertBefore(chip, countEl);
            } else {
                container.appendChild(chip);
            }
        });

        // Count = pending chips (+1 for a preserved price chip).
        const count = checked.length + (hasPriceChip ? 1 : 0);

        if (countEl) countEl.dataset.count = count;

        document.querySelectorAll('[data-filter-count]').forEach((element) => {
            element.dataset.filterCount = count;
        });
    }

    /**
     * Remove a PENDING chip (the shopper's own ✕) — unchecks the backing
     * checkbox and rebuilds, no fetch. Server price chips are real
     * <facet-remove> elements and are NOT matched here, so their ✕ keeps the
     * existing fetch-remove behavior.
     *
     * @param {object} event Delegated click event from .active-facets.
     * @returns {undefined}
     */
    onPendingFacetRemove(event) {

        const target = event.target.closest('[data-pending-remove]');

        if (!target) return;

        event.preventDefault();

        const checkbox = this.querySelector('[id="' + target.dataset.pendingRemove + '"]');

        if (checkbox) checkbox.checked = false;

        this._filtersDirty = true;

        this.renderOptimisticFacets();
    }

    /**
     * Watch the filter drawer open/close. bundle.js toggles the `.active` class on
     * [data-filter]. Closing the drawer by ANY means (Apply button, X, Esc or
     * tapping outside) COMMITS the current selection when the shopper changed
     * something — nothing is ever silently discarded. Opening resets the dirty
     * flag, so closing an unchanged drawer is a no-op. The Apply button also
     * applies directly and clears the flag, so it never double-applies here.
     *
     * @returns {undefined}
     */
    observeDrawerClose() {

        const form = this.querySelector('[data-filter]');

        if (!form || typeof MutationObserver === 'undefined') return;

        let wasOpen = form.classList.contains('active');

        const observer = new MutationObserver(() => {
            const isOpen = form.classList.contains('active');
            if (!wasOpen && isOpen) {
                this._filtersDirty = false;
            } else if (wasOpen && !isOpen && this._filtersDirty) {
                this.onApplyHandler();
            }
            wasOpen = isOpen;
        });

        observer.observe(form, { attributes: true, attributeFilter: ['class'] });
    }

    /**
     * On active filter click event.
     * 
     * @param {object} event Click event.
     * @returns {undefined}
     */
    onActiveFilterClick(event) {

        event.preventDefault();
        
        this.renderPage(new URL(event.currentTarget.href).searchParams.toString());
    }

    /**
     * Render page content.
     * 
     * @param {string} searchParams URL params.
     * @param {object} event Click event.
     * @param {boolean} updateURLHash update URL hash.
     * @returns {undefined}
     */
    renderPage(searchParams, event, updateURLHash = true) {

        const sections = this.getSections();

        document.getElementById('CollectionProductGrid').classList.add('loading');

        // Abort any still-pending fetch from a PREVIOUS renderPage (a newer
        // apply/remove/popstate supersedes it), then create one controller shared
        // by ALL sections of THIS render. Doing this here (not per-section) stops
        // sibling sections of the same render — e.g. Golden Goose's grid + header —
        // from aborting each other, which previously left the grid un-updated.
        if (this.currentController) this.currentController.abort();
        this.currentController = new AbortController();

        sections.forEach((section) => {
            if (!section?.section) {
                return;
            }
          let fetchPathname = window.location.pathname;
          let fetchSectionId = section.section;

          if (fetchPathname.includes('/pages/golden-goose')) {
              let handle = fetchPathname.split('/').pop();
              fetchPathname = '/collections/' + handle;
              if (fetchSectionId.includes('main-collection-product-grid') || fetchSectionId.includes('gg-grid')) {
                  fetchSectionId = 'golden-goose-collection-grid';
              }
          }

          const url = `${fetchPathname}?section_id=${fetchSectionId}&${searchParams}&options%5Bprefix%5D=none&type=product`;
          
          const filterDataUrl = element => element.url === url;
          this.filterData.some(filterDataUrl) ?
            this.renderSectionFromCache(filterDataUrl, event) :
            this.renderSectionFromFetch(url, event);
        });
    
        if (updateURLHash) this.updateURLHash(searchParams);
        this.scrollUp();
    }

    /**
     * Get sections.
     * 
     * @returns {undefined}
     */
    getSections() {
        let sections = [
            {
                id: 'collection',
                section: document.getElementById('main-collection-product-grid')?.dataset?.id,
            }
        ];
        
        let ggHeaderWrap = document.querySelector('[id^="shopify-section-template-"][id$="__golden-goose-collection-header"]');
        if (ggHeaderWrap) {
            let sectionId = ggHeaderWrap.id.replace('shopify-section-', '');
            sections.push({
                id: 'gg-header',
                section: sectionId
            });
        } else if (document.querySelector('.gg-col-header')) {
            sections.push({
                id: 'gg-header',
                section: 'golden-goose-collection-header'
            });
        }
        
        return sections;
    }

    /**
     * Enforce the store's in-stock default on the fetch URL from form/URL STATE
     * (not from a per-pill event). Rule: filter.v.availability=1 is present unless
     * the user explicitly selected Out-of-stock (=0). Idempotent — safe on any URL
     * and collapses any duplicate availability params to a single correct value.
     *
     * @param {string} url Fetch URL (may already contain availability params).
     * @returns {string} URL with exactly one correct availability param.
     */
    applyAvailabilityDefault(url) {
        try {
            const parsed = new URL(url, window.location.origin);
            const values = parsed.searchParams.getAll('filter.v.availability');
            parsed.searchParams.delete('filter.v.availability');
            if (values.includes('0')) {
                // User explicitly wants out-of-stock → honor it, drop forced in-stock.
                parsed.searchParams.append('filter.v.availability', '0');
            } else {
                // Default (and explicit in-stock) → force in-stock.
                parsed.searchParams.append('filter.v.availability', '1');
            }
            return `${parsed.pathname}?${parsed.searchParams.toString()}`;
        } catch (e) {
            return url;
        }
    }

    /**
     * Render section from fetch.
     *
     * @param {string} url String url.
     * @param {object} event Event.
     * @returns {undefined}
     */
    renderSectionFromFetch(url, event) {

        url = this.applyAvailabilityDefault(url);

        // The AbortController is owned by renderPage (shared across sibling
        // sections of this render); here we only consume its signal and lock the
        // drawer with a clean fade while loading.
        this.classList.add('filters--loading');

        fetch(url, { signal: this.currentController.signal })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.text();
            })
            .then((responseText) => {
                const html = responseText;
                const html_dom = new DOMParser().parseFromString(html, 'text/html');

                this.filterData = [...this.filterData, { html, url }];

                this.renderFilters(html_dom, event);
                this.renderProductGrid(html_dom, url);
                this.renderProductCount(html_dom);
                this.renderHeader(html_dom);

                this.classList.remove('filters--loading');
            })
            .catch((error) => {
                if (error.name === 'AbortError') return; // superseded by a newer fetch
                this.classList.remove('filters--loading');
                console.error('Error:', error);
            });
    }

    /**
     * Render section from catch.
     * 
     * @param {function} filterDataUrl A function.
     * @param {object} event Event.
     * @returns {undefined}
     */
    renderSectionFromCache(filterDataUrl, event) {

        const html = this.filterData.find(filterDataUrl).html;

        const html_dom = new DOMParser().parseFromString(html, 'text/html');

        this.renderFilters(html_dom, event);

        this.renderProductGrid(html_dom, this.filterData.find(filterDataUrl).url);

        this.renderProductCount(html_dom);

        this.renderHeader(html_dom);
    }

    /**
     * Render product grid.
     * 
     * @param {object} html_dom Page html dom.
     * @returns {undefined}
     */
    renderProductGrid(html_dom, url) {

        let productGrid = document.getElementById('CollectionProductGrid');

        if (productGrid) {

            let newProductGrid = html_dom.getElementById('CollectionProductGrid');

            if (newProductGrid) {
                
                productGrid.innerHTML = newProductGrid.innerHTML;

				document.getElementById('CollectionProductGrid').classList.remove('loading');

                this.renderQuickAddToCartPopup();

                const eventQuickView = new CustomEvent('init-quickview');
                window.dispatchEvent(eventQuickView);

                if(window.innerWidth > 1300 && document.body.clientHeight <= 1300) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    // For Safari
                    document.body.scrollTop = 0;
                    // For Chrome, Firefox, IE and Opera
                    document.documentElement.scrollTop = 0;

                } 
            }
        }
    }

    /**
     * Render product count.
     * 
     * @param {object} html_dom Page html dom.
     * @returns {undefined}
     */
     renderProductCount(html_dom) {

      let productQty = document.querySelectorAll('[data-collection-filter-product-qty]');

      let newproductQty = html_dom.querySelector('[data-collection-product-qty]');

      if(newproductQty){
        productQty.forEach(elem => {
          elem.innerHTML = newproductQty.innerHTML;
        });
      }
    }

    /**
     * Render header elements like breadcrumbs and subcategories.
     * 
     * @param {object} html_dom Page html dom.
     */
    renderHeader(html_dom) {
        let ggHeader = document.querySelector('.gg-col-header');
        if (ggHeader) {
            let newGgHeader = html_dom.querySelector('.gg-col-header');
            if (newGgHeader) {
                ggHeader.innerHTML = newGgHeader.innerHTML;
                
                // Re-bind horizontal scroll buttons since innerHTML removes listeners
                let wraps = document.querySelectorAll('.gg-col-types-wrap');
                wraps.forEach(wrap => {
                    let strip = wrap.querySelector('.gg-col-types');
                    let scrollBtn = wrap.querySelector('.gg-col-types-scroll-btn');
                    
                    if (strip && scrollBtn) {
                        scrollBtn.addEventListener('click', function() {
                            strip.scrollBy({ left: 200, behavior: 'smooth' });
                        });
                        
                        function checkOverflow() {
                            if (strip.scrollWidth > strip.clientWidth + 4) {
                                wrap.classList.add('has-overflow');
                            } else {
                                wrap.classList.remove('has-overflow');
                            }
                        }
                        
                        strip.addEventListener('scroll', function() {
                            checkOverflow();
                        });
                        
                        checkOverflow();
                    }
                });

                // Re-bind the Filter & Sort button inside the GG header.
                // Replacing innerHTML removes all event listeners, so we must
                // re-attach the openFilter call after every AJAX render.
                // Use querySelectorAll since the button may appear in both the
                // sizes row and the fallback bar.
                let filterBtns = ggHeader.querySelectorAll('[data-button-filter]');
                filterBtns.forEach(function(filterBtn) {
                    filterBtn.addEventListener('click', function(e) {
                        e.preventDefault();
                        let filterForm = document.querySelector('[data-filter]');
                        if (filterForm) {
                            filterForm.classList.remove('hidden');
                            filterForm.setAttribute('aria-hidden', 'false');
                            setTimeout(function() {
                                filterForm.classList.add('active');
                                let closeBtn = filterForm.querySelector('[data-filter-close]');
                                if (closeBtn) closeBtn.focus();
                            }, 5);
                        }
                    });
                });
            }
        }
    }


    /**
     * Render filters.
     * 
     * @param {object} html_dom Page html dom.
     * @returns {undefined}
     */
    renderFilters(html_dom, event) {

        const facetDetailsElements =
          html_dom.querySelectorAll('#CollectionFiltersForm .js-filter');

		const matchesIndex = element => {

			let result = element.dataset.index === event?.target.closest('.js-filter')?.dataset.index;

			return result;
		}

        const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    
        facetsToRender.forEach((element) => {
          document.querySelector(`.js-filter[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
        });
    
        this.renderActiveFacets(html_dom);

        this.renderAdditionalElements(html_dom);

        this.renderCounts();

        this.renderAllSelectedFilters();
    }

    /**
     * Render active facets.
     * 
     * @param {object} html_dom Page html dom.
     * @returns {undefined}
     */
    renderActiveFacets(html_dom) {
    
        const activeFacetElementSelectors = ['.active-facets'];

        activeFacetElementSelectors.forEach((selector) => {

            const activeFacetsElement = html_dom.querySelector(selector);

            if (!activeFacetsElement) return;

            document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
        });
    }

    /**
     * Render additional elements.
     * 
     * @param {object} html_dom Page html dom.
     * @returns {undefined}
     */
    renderAdditionalElements(html_dom) {

        const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];
    
        mobileElementSelectors.forEach((selector) => {

          if (!html_dom.querySelector(selector)) return;

          document.querySelector(selector).innerHTML = html_dom.querySelector(selector).innerHTML;

        });
    }

    /**
     * On history change.
     * 
     * @param {object} event Event.
     * @returns {undefined}
     */
    onHistoryChange(event) {

        const searchParams = event.state?.searchParams || '';

        this.renderPage(searchParams, null, false);
    }

    /**
     * Render counts.
     * 
     * @returns {undefined}
     */
    renderCounts() {

        let count = document.getElementById('activeFacetsCount').dataset.count;
        

        // if(window.screenMode == 'mobile' && window.location.search.includes('sort_by')) {
        //     count = parseInt(count) + 1;
        // }

        const urlParams = new URLSearchParams(window.location.search);
            
        if (urlParams.get("filter.v.availability") === "0") {
          // count = parseInt(count) + 1;
          //      document.querySelectorAll('[data-filter-count]').forEach(element => {
			// element.dataset.filterCount = parseInt(count);
   //      });
          // let content = `
          //     <facet-remove>
          //         <a href="/" class="active-facets__button">
          //             out of stock
          //             <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          //                 <rect width="32" height="32" fill="none" stroke="none"/>
          //                 <path fill-rule="evenodd" clip-rule="evenodd" d="M8.26849 8.2685C8.44039 8.09659 8.67355 8.00002 8.91667 8.00002C9.15978 8.00002 9.39294 8.09659 9.56485 8.2685L23.8248 22.5285C23.9967 22.7004 24.0933 22.9336 24.0933 23.1767C24.0933 23.4198 23.9967 23.6529 23.8248 23.8249C23.6529 23.9968 23.4198 24.0933 23.1767 24.0933C22.9335 24.0933 22.7004 23.9968 22.5285 23.8249L8.26849 9.56486C8.09658 9.39296 8 9.1598 8 8.91668C8 8.67357 8.09658 8.44041 8.26849 8.2685Z" stroke="none"/>
          //                 <path fill-rule="evenodd" clip-rule="evenodd" d="M8.2686 23.8248C8.09669 23.6529 8.00011 23.4198 8.00011 23.1767C8.00011 22.9335 8.09669 22.7004 8.2686 22.5285L22.5286 8.26849C22.7005 8.09658 22.9336 8 23.1768 8C23.4199 8 23.653 8.09658 23.8249 8.26849C23.9969 8.44039 24.0934 8.67355 24.0934 8.91667C24.0934 9.15978 23.9969 9.39294 23.8249 9.56485L9.56496 23.8248C9.39305 23.9967 9.15989 24.0933 8.91678 24.0933C8.67366 24.0933 8.4405 23.9967 8.2686 23.8248Z" stroke="none"/>
          //             </svg>
          //         </a>
          //     </facet-remove>`
          // document.getElementById('activeFacetsCount').innerHTML = document.getElementById('activeFacetsCount').innerHTML  + content;
        }

        document.querySelectorAll('[data-filter-count]').forEach(element => {
			element.dataset.filterCount = parseInt(count);
        });
    }

    /**
     * Render all selected filters text.
     * 
     * @returns {undefined}
     */
    renderAllSelectedFilters() {
        var accordionItems = this.querySelectorAll('[data-accordion-item]');

        if(accordionItems) {

            // Load all filters
            accordionItems.forEach((item) => {
                this.updateHeadingText(item);
            });

           // this.updatePriceHeading();
        }
    }

    /**
     * Render all event buttons that open the quick add to cart popup.
     * 
     * @returns {undefined}
     */
    renderQuickAddToCartPopup() {
        const eventQuickAddToCart = new CustomEvent('init-quick-add-to-cart');
        window.dispatchEvent(eventQuickAddToCart);
    }

    /**
     * Update filter heading text.
     * 
     * @param {object} item Accordion Item.
     * @returns {undefined}
     */
    updateHeadingText(item) {

        // SIZE | COLOR | SORTING | PRODUCT TYPE - options
        let heading = item.querySelector('[data-accordion-heading]');
        let tag = heading.querySelector('[data-accordion-selected-filters]');
        let checkboxInputs = item.querySelectorAll('input[type="checkbox"]');
        checkboxInputs = Array.from(checkboxInputs).filter((input) => input.checked);

        if(checkboxInputs.length) {

            let appliedFilters = '';

            // Load all selected filters
            checkboxInputs.forEach((input, index) => {

                let label = item.querySelector(`label[for='${input.id}']`);

                if(tag && label) {

                    if(index == 0) {
                        //Only one filter selected
                        appliedFilters = `: ${label.innerText}`;
                    }
                    else {
                        //More than one filter selected
                        appliedFilters += ` / ${label.innerText}`;
                    }
                }
            });

        }
        else {
            tag.innerText = '';
        }
    }

    /**
     * Update price filter heading text.
     * 
     * @returns {undefined}
     */
    updatePriceHeading() {

        // PRICE options
        let numberInputs = this.querySelectorAll('input[type="number"]');

        if(numberInputs.length) {

            let minInput = numberInputs[0];
            let maxInput = numberInputs[1];

            if(minInput.value && maxInput.value) {

                let item = minInput.closest('[data-accordion-item]');
                let heading = item.querySelector('[data-accordion-heading]');
                let tag = heading.querySelector('[data-accordion-selected-filters]');
                let min = minInput.getAttribute('min');
                let max = maxInput.getAttribute('max');

                if((parseInt(minInput.value) == parseInt(min))&&(parseInt(maxInput.value) == parseInt(max))) {
                    tag.innerText = '';
                } else {
                    tag.innerText = `: ${minInput.value} - ${maxInput.value}`;
                }
            }
        }
    }

    /**
     * Update URL hash.
     * 
     * @returns {undefined}
     */
    updateURLHash(searchParams) {
      history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    }

    /**
     * Set elements on top
     * 
     * @returns {undefined}
     */
     setTop() {
        let collectionFilters = document.querySelector(".collection__filters");
        let collectionBreadcrumbs = document.querySelector(".collection .breadcrumbs");
        let height = 90;
        
        if (document.querySelector(".alert-message__container")) {
            height = 90 + document.querySelector(".alert-message__container").clientHeight;
        } 

        if (collectionFilters) {
            collectionFilters.style.top = height + "px";
        } 

        if (collectionBreadcrumbs) {
            collectionBreadcrumbs.style.top = height + "px";
        } 
        
    }


}

/**
 * Close ("✕") icon markup inlined from snippets/icon-close.liquid. Used as the
 * remove affordance on the optimistic pending chips rendered by
 * renderOptimisticFacets, matching the server active-facets chip markup.
 *
 * @type {string}
 */
CollectionFilters.CLOSE_ICON_SVG =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<rect width="32" height="32" fill="none" stroke="none"/>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M8.26849 8.2685C8.44039 8.09659 8.67355 8.00002 8.91667 8.00002C9.15978 8.00002 9.39294 8.09659 9.56485 8.2685L23.8248 22.5285C23.9967 22.7004 24.0933 22.9336 24.0933 23.1767C24.0933 23.4198 23.9967 23.6529 23.8248 23.8249C23.6529 23.9968 23.4198 24.0933 23.1767 24.0933C22.9335 24.0933 22.7004 23.9968 22.5285 23.8249L8.26849 9.56486C8.09658 9.39296 8 9.1598 8 8.91668C8 8.67357 8.09658 8.44041 8.26849 8.2685Z" stroke="none"/>' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M8.2686 23.8248C8.09669 23.6529 8.00011 23.4198 8.00011 23.1767C8.00011 22.9335 8.09669 22.7004 8.2686 22.5285L22.5286 8.26849C22.7005 8.09658 22.9336 8 23.1768 8C23.4199 8 23.653 8.09658 23.8249 8.26849C23.9969 8.44039 24.0934 8.67355 24.0934 8.91667C24.0934 9.15978 23.9969 9.39294 23.8249 9.56485L9.56496 23.8248C9.39305 23.9967 9.15989 24.0933 8.91678 24.0933C8.67366 24.0933 8.4405 23.9967 8.2686 23.8248Z" stroke="none"/>' +
    '</svg>';

customElements.define('collection-filters', CollectionFilters);






class PriceRange extends HTMLElement {
    constructor() {
      super();
      this.querySelectorAll('input')
        .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));
  
      this.setMinAndMaxValues();
    }
  
    onRangeChange(event) {
      this.adjustToValidValues(event.currentTarget);
      this.setMinAndMaxValues();
    }
  
    setMinAndMaxValues() {
      const inputs = this.querySelectorAll('input');
      const minInput = inputs[0];
      const maxInput = inputs[1];
      if (maxInput.value) minInput.setAttribute('max', maxInput.value);
      if (minInput.value) maxInput.setAttribute('min', minInput.value);
      if (minInput.value === '') maxInput.setAttribute('min', 0);
      if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
    }
  
    adjustToValidValues(input) {
      const value = Number(input.value);
      const min = Number(input.getAttribute('min'));
      const max = Number(input.getAttribute('max'));
  
      if (value < min) input.value = min;
      if (value > max) input.value = max;
    }
  }
  
  customElements.define('price-range', PriceRange);






  /**
 * Price Range slider
 * 
 * @module price-range-slider
 * @version 1.0.0
 * @requires module:collection-filters
 * @extends HTMLElement
 */
 class PriceRangeSlider extends HTMLElement {

    /**
     * Price Ranger Slider web component.
     * 
     * @constructor
     */
    constructor() {
        super();
        this.initEvents();
    }

    /**
       * Init component events.
       * 
       * @returns {undefined}
       */
    initEvents() {

        let element = document.createElement('div');
        this.append(element);

        let step = this.dataset.priceRangeSlideStep || 1;
        let min = this.dataset.priceRangeSlideMin || 0;
        let max = this.dataset.priceRangeSlideMax || 100;
        let inputMin = document.querySelector(this.dataset.inputMinId);
        let inputMax = document.querySelector(this.dataset.inputMaxId);
        let startMin, startMax;
        this.filterInputs = document.querySelectorAll(this.dataset.inputMinId +' , '+ this.dataset.inputMaxId);

        if(inputMin.value && inputMax.value) {
            startMin = inputMin.value;
            startMax = inputMax.value;
        }
        else {
            startMin = min;
            startMax = max;
        }

        // Init noUiSlider
        noUiSlider.create(element, {
            start: [startMin, startMax],
            connect: true,
            step: Number(step),
            range: {
                'min': Number(min),
                'max': Number(max)
            },
            handleAttributes: [
              { 'aria-label': 'Lower price' },
              { 'aria-label': 'Upper price' },
            ]

        });

        this.filterInputs.forEach((input, indexInput) => { 
            input.addEventListener('input', () => {
                element.noUiSlider.setHandle(indexInput, input.value);
            })
        });

        element.noUiSlider.on('update', (values, handle) => {
            this.filterInputs[handle].value = values[handle];
        });

        // Dispatch Input event after value is updated
        element.noUiSlider.on('set', (values, handle) => { 

            var event = new Event('input', {
                bubbles: true,
                cancelable: true,
            });

            if((parseInt(inputMin.value) == parseInt(min))&&(parseInt(inputMax.value) == parseInt(max))) {
                inputMin.removeAttribute('name');
                inputMax.removeAttribute('name');
            }else {
                inputMin.setAttribute('name', 'filter.v.price.gte');
                inputMax.setAttribute('name', 'filter.v.price.lte');
            }
            
            this.filterInputs[handle].dispatchEvent(event);

        });

        window.addEventListener('load', () => {
            const queryString = window.location.search;
            if((queryString.includes('filter.v.price.gte'))||(queryString.includes('filter.v.price.lte'))){
                inputMin.setAttribute('name', 'filter.v.price.gte');
                inputMax.setAttribute('name', 'filter.v.price.lte');
            }else{
                inputMin.removeAttribute('name');
                inputMax.removeAttribute('name');
            }
        });

    }
}

customElements.define('price-range-slider', PriceRangeSlider);





/**
 * Facet remove
 * 
 * @module facet-remove
 * @version 1.0.0
 * @requires module:collection-filters
 * @extends HTMLElement
 */
 class FacetRemove extends HTMLElement {

    connectedCallback() {

        if (this._initialized || !this.querySelector('a')) return;
        this._initialized = true;

        this.querySelector('a').addEventListener('click', (event) => {

            event.preventDefault();

            const form = this.closest('collection-filters') || document.querySelector('collection-filters');

            form.onActiveFilterClick(event);
        });
    }
}

customElements.define('facet-remove', FacetRemove);
